import { GeoJSONSource } from "mapbox-gl";

import { TileJSONResponse } from "api/shapefile";

import { ShapefileFileType } from "models/projects";

import { emptyFeatureCollection } from "components/map/utils/emptyFeatureCollection";
import {
  safeRemoveLayerAndSource,
  safeRemoveLayers
} from "components/map/utils/layerOperations";
import { IEditableProjectLayer } from "components/project/layers/hooks";
import { Shapefile } from "components/project/shapefiles/queries";
import {
  allShapefileTypes,
  shapefileLayerType
} from "components/project/shapefiles/shapefiles.constants";

import { getDifference } from "../getDifference";
import { addGeoTiffLayer } from "./addGeoTiffLayer";
import { addShapetileLayers, updateShapetileFilters } from "./addShapetileLayers";

interface IOnShapefilesChangedParams {
  addLayer;
  checkedShapefiles: Set<string>;
  lastCheckedShapefiles: React.MutableRefObject<Set<string>>;
  layerList: IEditableProjectLayer[];
  mapbox: mapboxgl.Map;
  shapefileFeatures;
  shapefileListSorted: Shapefile[];
  tileJSONMap: Record<string, TileJSONResponse>;
}
export function onShapefilesChanged(params: IOnShapefilesChangedParams): void {
  const {
    addLayer,
    checkedShapefiles,
    layerList,
    mapbox,
    shapefileFeatures,
    shapefileListSorted,
    tileJSONMap
  } = params;
  if (!mapbox) {
    return;
  }

  /**
   * Remove layers that are no longer checked
   */
  const lastChecked = params.lastCheckedShapefiles.current || new Set<string>();
  const shapefilesToRemove: string[] = getDifference(lastChecked, checkedShapefiles);
  params.lastCheckedShapefiles.current = new Set(checkedShapefiles);

  for (const layerIdToRemove of shapefilesToRemove) {
    try {
      // layerIdToRemove is either projectShapefileId or shapefileId (the sourceId used when adding)
      const geojsonLayerName = `${layerIdToRemove}${shapefileLayerType.label}`;
      const isGeoTiff =
        !mapbox.getLayer(geojsonLayerName) && mapbox.getLayer(layerIdToRemove);
      if (isGeoTiff) {
        safeRemoveLayerAndSource(mapbox, layerIdToRemove);
        continue;
      }

      const source = mapbox.getSource(layerIdToRemove);
      if (source) {
        // For GeoJSON sources, set data to empty before removing to help with GC
        if ((source as GeoJSONSource).setData) {
          (source as GeoJSONSource).setData({ ...emptyFeatureCollection });
        }

        // Remove all associated layers for vector tile sources
        const layerNames = allShapefileTypes.map((type) => `${layerIdToRemove}${type}`);
        safeRemoveLayers(mapbox, layerNames);

        // Remove the source
        if (mapbox.getSource(layerIdToRemove)) {
          mapbox.removeSource(layerIdToRemove);
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Unable to remove layer", err);
    }
  }

  if (!shapefileListSorted) {
    return;
  }

  /**
   * Either add the new shapefile sources or update the existing ones
   */

  for (const shapefile of shapefileListSorted) {
    const layerName = shapefile.projectShapefileId || shapefile.shapefileId;
    const isGeoTiff = shapefile.fileType === ShapefileFileType.GeoTiff;

    // Process GeoJSON feature properties once before add/update
    const hasSource = mapbox && mapbox.getSource(layerName);

    if (mapbox && !hasSource) {
      if (isGeoTiff) {
        addGeoTiffLayer({ mapbox, layerName, shapefile, addLayer });
      } else {
        // New shapetile approach
        const tileJSON = tileJSONMap[shapefile.shapefileId];
        if (!tileJSON) {
          continue;
        }
        addShapetileLayers(
          mapbox,
          shapefile,
          shapefileFeatures,
          layerList,
          addLayer,
          tileJSON
        );
      }
    } else {
      // Would need to change if geotiff required source update
      if (isGeoTiff) {
        if (
          shapefile?.boundingBox?.length === 4 &&
          mapbox.getPaintProperty(layerName, "raster-opacity") !== shapefile.opacity &&
          mapbox.getSource(layerName)
        ) {
          mapbox.setPaintProperty(layerName, "raster-opacity", shapefile.opacity || 0.8);
        }
      } else {
        // Shapetile update
        updateShapetileFilters(mapbox, shapefile, shapefileFeatures, layerList);
      }
    }
  }
}
